import { useEffect, useRef, useState } from 'react';
import { animate, useMotionValue, useTransform, motion } from 'framer-motion';

/**
 * ProgressRing — Animated SVG circular progress ring.
 * Props:
 *   progress    {number}  0-100
 *   size        {number}  diameter px (default 120)
 *   strokeWidth {number}  ring thickness (default 10)
 *   color       {string}  hex color (default teal-500)
 *   label       {string}  optional label below ring
 *   animate     {boolean} animate on mount/change (default true)
 *   className   {string}
 */
export default function ProgressRing({
  progress = 0,
  size = 120,
  strokeWidth = 10,
  color = '#14b8a6',
  label,
  animate: shouldAnimate = true,
  className = '',
}) {
  const clamped = Math.max(0, Math.min(100, progress));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Framer-motion value drives dashoffset
  const motionValue = useMotionValue(shouldAnimate ? 0 : clamped);
  const dashOffset = useTransform(motionValue, [0, 100], [circumference, 0]);

  // Counter display (avoids layout jitter from framer-motion text)
  const [display, setDisplay] = useState(shouldAnimate ? 0 : Math.round(clamped));

  useEffect(() => {
    const controls = animate(motionValue, clamped, {
      duration: shouldAnimate ? 1.3 : 0,
      ease: [0.34, 1.08, 0.64, 1],
    });
    const unsub = motionValue.on('change', (v) => setDisplay(Math.round(v)));
    return () => {
      controls.stop();
      unsub();
    };
  }, [clamped, shouldAnimate]);

  // Adaptive color by progress level when using default teal
  const ringColor =
    color !== '#14b8a6'
      ? color
      : clamped >= 80
      ? '#10b981'
      : clamped >= 50
      ? '#14b8a6'
      : clamped >= 25
      ? '#f59e0b'
      : '#ef4444';

  // Stable gradient id per component instance
  const gradId = useRef(`prg-${Math.random().toString(36).slice(2, 9)}`).current;

  return (
    <div
      className={`flex flex-col items-center gap-1 ${className}`}
      style={{ width: size, minWidth: size }}
    >
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)' }}
          role="img"
          aria-label={`Progress: ${display}%`}
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={ringColor} stopOpacity="1" />
              <stop offset="100%" stopColor={ringColor} stopOpacity="0.65" />
            </linearGradient>
          </defs>
          {/* Track */}
          <circle
            cx={center} cy={center} r={radius}
            fill="none"
            stroke="rgba(51,65,85,0.8)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Animated arc */}
          <motion.circle
            cx={center} cy={center} r={radius}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            style={{ strokeDashoffset: dashOffset }}
          />
        </svg>

        {/* Center percentage text */}
        <div
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span
            className="font-bold tabular-nums leading-none"
            style={{ fontSize: size * 0.22, color: ringColor }}
          >
            {display}%
          </span>
        </div>
      </div>

      {label && (
        <p className="text-xs text-slate-400 font-medium text-center mt-0.5 max-w-full truncate px-1">
          {label}
        </p>
      )}
    </div>
  );
}
